import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, InfoNote } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ShieldAlert, PlugZap, Activity, Users, FlaskConical, Eye } from "lucide-react";

export default function Administration() {
  const { bootstrap, roleCode, operatingMode, refreshSettings } = useApp();
  const [integrations, setIntegrations] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [ver, setVer] = useState({ version: "1.1", effective_date: "2026-07-01", revision_notes: "Synthetic refinement of Criterion D wording." });
  const [busy, setBusy] = useState(false);

  const loadPolicies = () => api.policies().then(setPolicies).catch(() => {});
  useEffect(() => { api.integrations().then(setIntegrations).catch(() => {}); loadPolicies(); }, []);

  const createVersion = async () => {
    setBusy(true);
    try {
      await api.createPolicyVersion("policy-rpp-001", { ...ver, role_code: roleCode });
      toast.success(`Policy version ${ver.version} created — prior evaluations remain linked to their original version`);
      loadPolicies();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const setMode = async (mode) => {
    try {
      await api.setOperatingMode({ operating_mode: mode, role_code: roleCode });
      await refreshSettings();
      toast.success(`Operating mode: ${mode}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Only System Administrator may change the operating mode."); }
  };

  return (
    <div>
      <PageHeader testid="admin-header" title="Administration"
        subtitle="Manage synthetic policies, inspect integrations, and review system health. Administrator persona only." />
      <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        This prototype is not authorized for storage or processing of PHI. It is not HIPAA compliant and not for production use.
      </div>

      <Tabs defaultValue="policies">
        <TabsList className="mb-4">
          <TabsTrigger value="policies" data-testid="admin-tab-policies">Policies & Rules</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Demo Users</TabsTrigger>
          <TabsTrigger value="tests" data-testid="admin-tab-tests">Test Definitions</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="admin-tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="health" data-testid="admin-tab-health">System Health</TabsTrigger>
          <TabsTrigger value="mode" data-testid="admin-tab-mode">Operating Mode</TabsTrigger>
        </TabsList>

        <TabsContent value="policies">
          <Card className="p-4">
            <div className="mb-3 text-sm font-semibold text-slate-800">Synthetic Policy Management</div>
            {policies.map((p) => (
              <div key={p.id} className="mb-3 rounded-md border border-slate-200 p-3 text-xs">
                <div className="font-semibold text-slate-800">{p.policy_code} — {p.name}</div>
                <div className="mt-1 text-slate-500">Versions: {p.versions?.map((v) => `v${v.version}${v.active ? " (active)" : ""}`).join(", ")}</div>
              </div>
            ))}
            <div className="mt-2 rounded-md border border-teal-200 bg-teal-50/50 p-3">
              <div className="mb-2 text-xs font-semibold text-teal-900">Create new synthetic policy version (demonstrates version linkage — AT-07)</div>
              <div className="grid gap-2 sm:grid-cols-3">
                <div><Label className="text-[10px]">Version</Label><Input data-testid="admin-ver-version" value={ver.version} onChange={(e) => setVer({ ...ver, version: e.target.value })} /></div>
                <div><Label className="text-[10px]">Effective date</Label><Input data-testid="admin-ver-date" value={ver.effective_date} onChange={(e) => setVer({ ...ver, effective_date: e.target.value })} /></div>
                <div><Label className="text-[10px]">Revision notes</Label><Input data-testid="admin-ver-notes" value={ver.revision_notes} onChange={(e) => setVer({ ...ver, revision_notes: e.target.value })} /></div>
              </div>
              <Button size="sm" className="mt-3 bg-teal-600 hover:bg-teal-700" data-testid="admin-create-version" disabled={busy} onClick={createVersion}>Create Version</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card className="p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Users className="h-4 w-4" /> Demo Users (Personas)</div>
            <div className="grid gap-2 sm:grid-cols-2">
              {bootstrap?.personas?.map((p) => (
                <div key={p.role_code} className="rounded-md border border-slate-200 p-3 text-xs">
                  <div className="font-semibold text-slate-800">{p.name}</div>
                  <div className="text-slate-500">{p.role}</div>
                  <div className="mt-1 font-mono text-[10px] text-slate-400">role_code: {p.role_code}</div>
                </div>
              ))}
            </div>
            <InfoNote className="mt-3">Prototype 0.1 uses a frictionless “Viewing As” role switcher. Structure supports adding real server-side authentication/RBAC later without redesigning workflows.</InfoNote>
          </Card>
        </TabsContent>

        <TabsContent value="tests">
          <Card className="p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><FlaskConical className="h-4 w-4" /> Test Definitions</div>
            {bootstrap?.tests?.map((t) => (
              <div key={t.id} className="mb-2 flex items-center justify-between rounded-md border border-slate-200 p-3 text-xs">
                <div><span className="font-semibold text-slate-800">{t.name}</span><div className="text-slate-500">{t.code_demo} · {t.category}</div></div>
                <span className={`rounded px-1.5 py-0.5 text-[10px] ${t.in_scope ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"}`}>{t.in_scope ? "In DEMO-RPP-001 scope" : "Out of scope"}</span>
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="integrations">
          <Card className="p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><PlugZap className="h-4 w-4" /> Future Integration Adapters</div>
            <p className="mb-3 text-xs text-slate-500">Interfaces/placeholders only. No live connectivity is simulated in Prototype 0.1.</p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {integrations.map((i) => (
                <div key={i.id} className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-3" data-testid={`integration-${i.id}`}>
                  <div className="text-sm font-medium text-slate-700">{i.name}</div>
                  <div className="text-[10px] text-slate-400">{i.category}</div>
                  <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700">{i.status}</div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="health">
          <Card className="p-4">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Activity className="h-4 w-4" /> System Health</div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {[["Application API", "Operational"], ["AI Evidence Extraction", "Operational (LLM primary · demo fallback)"], ["Rules Engine", "Operational"], ["Policy Service", "Operational"], ["Audit Service", "Operational · hash-chained"], ["Database", "Operational"]].map(([k, v]) => (
                <div key={k} className="rounded-md border border-slate-200 p-3 text-xs">
                  <div className="font-medium text-slate-700">{k}</div>
                  <div className="mt-1 flex items-center gap-1.5 text-emerald-700"><span className="h-2 w-2 rounded-full bg-emerald-500" /> {v}</div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="mode">
          <Card className="p-4" data-testid="operating-mode-control">
            <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><Eye className="h-4 w-4" /> Operating Mode</div>
            <p className="mb-3 text-xs text-slate-500">In SHADOW mode, RequiSure records recommendations for validation only and a persistent banner is shown; the existing operational workflow always remains authoritative. RequiSure never holds, releases, or transmits a real order in either mode within this prototype.</p>
            <div className="flex items-center gap-2">
              {["ACTIVE", "SHADOW"].map((m) => (
                <Button key={m} size="sm" variant={operatingMode === m ? "default" : "outline"}
                  className={operatingMode === m ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                  data-testid={`mode-${m.toLowerCase()}`} onClick={() => setMode(m)}>
                  {m}{operatingMode === m ? " (current)" : ""}
                </Button>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
